'use client';

import Link from 'next/link';
import { useRouter } from 'next/navigation';

const FEATURED_JOURNEYS = [
  { name: 'Venice Simplon-Orient-Express', route: 'London — Paris — Venice', nights: '2 nights', operator: 'Belmond', price: 'From $5,400 per guest', tags: ['Orient Express', 'Europe', 'Iconic'] },
  { name: 'Rovos Rail — Pride of Africa', route: 'Cape Town to Dar es Salaam', nights: '15 nights', operator: 'Rovos Rail', price: 'From $18,500 per guest', tags: ['Africa', 'Epic Journey', 'Rovos'] },
  { name: 'Rocky Mountaineer — First Passage', route: 'Vancouver to Banff', nights: '2 nights', operator: 'Rocky Mountaineer', price: 'From $4,400 per guest', tags: ['Canada', 'Mountains', 'Scenic'] },
  { name: 'Maharajas\' Express — Heritage of India', route: 'Delhi to Mumbai', nights: '7 nights', operator: 'IRCTC', price: 'From $9,600 per guest', tags: ['India', 'Heritage', 'Palaces'] },
  { name: 'Seven Stars in Kyushu', route: 'Hakata round-trip', nights: '3 nights', operator: 'JR Kyushu', price: 'From $11,800 per guest', tags: ['Japan', 'Exclusive', '28 guests'] },
  { name: 'Royal Scotsman — Grand Tour', route: 'Edinburgh round-trip', nights: '7 nights', operator: 'Belmond', price: 'From $12,800 per guest', tags: ['Scotland', 'Highlands', 'Belmond'] },
];

const FEATURES = [
  { icon: '🛏', title: 'Suite cabins', desc: 'Private ensuite cabins with panoramic windows and butler service.' },
  { icon: '🍷', title: 'Michelin-inspired dining', desc: 'Multi-course tasting menus paired with fine wines each evening.' },
  { icon: '🏰', title: 'Curated off-train excursions', desc: 'Private access at every stop — castles, wineries, safari camps.' },
];

export default function RailPage() {
  const router = useRouter();

  return (
    <div className="min-h-screen bg-[var(--background)] text-[var(--foreground)]">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[var(--surface-2)]/95 backdrop-blur-xl border-b border-[var(--border)] h-16 flex items-center px-6">
        <Link href="/" className="text-[var(--primary)] font-bold tracking-widest uppercase mr-8" style={{ fontFamily: 'var(--font-serif)' }}>WorldwayLuxe</Link>
        <div className="flex items-center gap-6 text-sm text-[var(--muted-foreground)]">
          <Link href="/cruises" className="hover:text-[var(--primary)] transition-colors">Cruises</Link>
          <Link href="/safari" className="hover:text-[var(--primary)] transition-colors">Safari</Link>
          <Link href="/tailor-made" className="hover:text-[var(--primary)] transition-colors">Tailor-Made</Link>
          <Link href="/polar-expeditions" className="hover:text-[var(--primary)] transition-colors">Polar</Link>
        </div>
        <div className="ml-auto flex items-center gap-3">
          <Link href="/auth/login" className="text-sm text-[var(--muted-foreground)] hover:text-[var(--foreground)] px-4 py-2">Sign In</Link>
          <Link href="/auth/register" className="btn-primary text-sm px-5 py-2 rounded-lg">Join Now</Link>
        </div>
      </nav>
      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#050810] via-[var(--background)] to-[#0D1525]" />
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(ellipse 80% 60% at 50% 20%, rgba(201,168,76,0.15) 0%, transparent 70%)' }} />
        <div className="relative z-10 max-w-5xl mx-auto px-6">
          <p className="label-caps mb-3 text-[var(--primary)]">Grand rail voyages</p>
          <h1 className="text-4xl md:text-6xl font-bold mb-4" style={{ fontFamily: 'var(--font-serif)' }}>
            Luxury Rail Journeys<br />
            <span className="gold-text-gradient">The Golden Age, Reimagined.</span>
          </h1>
          <p className="text-lg text-[var(--muted-foreground)] mb-6 max-w-xl">
            The romance of the golden age of rail, reimagined — Belmond&apos;s Venice Simplon-Orient-Express, Royal Scotsman, Rocky Mountaineer, Rovos Rail, Maharajas&apos; Express and Japan&apos;s Seven Stars in Kyushu.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
            {FEATURES?.map((f) => (
              <div key={f?.title} className="glass-card rounded-xl p-4 border border-[rgba(201,168,76,0.1)]">
                <div className="text-2xl mb-2">{f?.icon}</div>
                <div className="text-sm font-bold mb-1">{f?.title}</div>
                <div className="text-xs text-[var(--muted-foreground)]">{f?.desc}</div>
              </div>
            ))}
          </div>
          <div className="flex gap-4">
            <button onClick={() => router?.push('/booking?vertical=rail')} className="btn-primary px-8 py-3 rounded-xl text-sm font-semibold">Design Your Rail Journey</button>
            <Link href="/ai-concierge" className="btn-ghost px-8 py-3 rounded-xl text-sm font-semibold border border-[var(--border)]">Ask Concierge</Link>
          </div>
        </div>
      </section>
      {/* Featured Journeys */}
      <section className="py-16 border-t border-[var(--border)]">
        <div className="max-w-5xl mx-auto px-6">
          <p className="label-caps mb-2">Featured Rail Journeys</p>
          <h2 className="text-2xl font-bold mb-8" style={{ fontFamily: 'var(--font-serif)' }}>The World&apos;s Most <span className="gold-text-gradient">Legendary Trains</span></h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {FEATURED_JOURNEYS?.map((j) => (
              <div key={j?.name} className="glass-card rounded-xl p-5 hover:border-[rgba(201,168,76,0.3)] transition-all group cursor-pointer" onClick={() => router?.push('/booking?vertical=rail')}>
                <div className="text-2xl mb-3">🚂</div>
                <h3 className="text-sm font-bold mb-1 group-hover:text-[var(--primary)] transition-colors" style={{ fontFamily: 'var(--font-serif)' }}>{j?.name}</h3>
                <p className="text-xs text-[var(--muted-foreground)] mb-1">{j?.route}</p>
                <p className="text-xs text-[var(--muted-foreground)] mb-1">{j?.nights} · {j?.operator}</p>
                <div className="flex flex-wrap gap-1 mb-3">
                  {j?.tags?.map((tag) => (
                    <span key={tag} className="text-[10px] px-2 py-0.5 rounded-full bg-[rgba(201,168,76,0.1)] text-[var(--primary)]">{tag}</span>
                  ))}
                </div>
                <p className="text-xs font-semibold text-[var(--primary)]">{j?.price}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* FAQ */}
      <section className="py-16 border-t border-[var(--border)]">
        <div className="max-w-5xl mx-auto px-6">
          <p className="label-caps mb-6">FAQ</p>
          <div className="space-y-4">
            {[
              { q: 'What is the dress code?', a: 'Elegant daywear; jacket & tie or gown for dinner on select trains. The Orient-Express and Rovos Rail have formal dinner requirements.' },
              { q: 'Are single cabins available?', a: 'Most trains offer single occupancy at a supplement. Some departures waive the single supplement — ask our specialists.' },
              { q: 'Can I combine rail with other travel?', a: 'Absolutely. We frequently combine luxury rail with private jets, safaris, villas and yacht charters for seamless multi-modal journeys.' },
            ]?.map((faq) => (
              <div key={faq?.q} className="glass-card rounded-xl p-5">
                <div className="text-sm font-bold mb-2 text-[var(--primary)]">{faq?.q}</div>
                <div className="text-xs text-[var(--muted-foreground)] leading-relaxed">{faq?.a}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
      <footer className="border-t border-[var(--border)] py-8">
        <div className="max-w-5xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="text-xs text-[var(--muted-foreground)]">© 2026 Worldway Travels Group · All rights reserved.</div>
          <div className="flex items-center gap-6 text-xs text-[var(--muted-foreground)]">
            <Link href="/" className="hover:text-[var(--primary)] transition-colors">Home</Link>
            <Link href="/cruises" className="hover:text-[var(--primary)] transition-colors">Cruises</Link>
            <Link href="/safari" className="hover:text-[var(--primary)] transition-colors">Safari</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
